/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  MapPin,
  Minus,
  Plus,
  Route as RouteIcon,
  Shield,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserX,
  X,
} from "lucide-react";
import SupervisorMap from "./SupervisorMap";
import { securityApi } from "./api";
import { toast } from "../ui";
import SosPanel from "../sos/SosPanel";
import "./SecurityPage.css";

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch (_) { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (_) { return iso; }
}

// ── Guards tab ────────────────────────────────────────────────────────────────
function GuardsTab() {
  const [guards, setGuards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone_number: "", role: "GUARD" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await securityApi.listGuards({});
      setGuards(Array.isArray(data) ? data : data.guards || []);
    } catch (e) {
      toast.error("Could not load guards");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (g) => {
    setBusy(g.guard_id);
    try {
      await securityApi.patchGuard(g.guard_id, { is_active: !g.is_active });
      toast.success(g.is_active ? `${g.name} deactivated` : `${g.name} activated`);
      await load();
    } catch (e) {
      toast.error(e?.message || "Could not update guard");
    } finally {
      setBusy(null);
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone_number.trim()) return;
    setSaving(true);
    try {
      await securityApi.createGuard({ name: form.name.trim(), phone_number: form.phone_number.trim(), role: form.role });
      toast.success("Guard added — OTP sent to their phone");
      setAddOpen(false);
      setForm({ name: "", phone_number: "", role: "GUARD" });
      await load();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not add guard");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="sp-empty">Loading guards…</div>;

  return (
    <div>
      <div className="sp-row-between sp-mb">
        <span className="sp-count">{guards.length} guard{guards.length !== 1 ? "s" : ""}</span>
        <button className="sp-btn sp-btn--primary" onClick={() => setAddOpen(true)}>
          <Plus size={14} /> Add guard
        </button>
      </div>

      {addOpen && (
        <form className="sp-form-card" onSubmit={submitAdd}>
          <div className="sp-form-head">
            Add guard
            <button type="button" className="sp-form-close" onClick={() => setAddOpen(false)}><X size={16} /></button>
          </div>
          <div className="sp-field">
            <label>Full name</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ramesh Kumar" required />
          </div>
          <div className="sp-field">
            <label>Phone (10-digit)</label>
            <input value={form.phone_number} onChange={e => setForm(p => ({ ...p, phone_number: e.target.value }))} placeholder="9XXXXXXXXX" maxLength={15} required />
          </div>
          <div className="sp-field">
            <label>Role</label>
            <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
              <option value="GUARD">Guard</option>
              <option value="SUPERVISOR">Supervisor</option>
            </select>
          </div>
          <div className="sp-form-actions">
            <button type="button" className="sp-btn sp-btn--ghost" onClick={() => setAddOpen(false)}>Cancel</button>
            <button type="submit" className="sp-btn sp-btn--primary" disabled={saving}>{saving ? "Adding…" : "Add & send OTP"}</button>
          </div>
        </form>
      )}

      <div className="sp-list">
        {guards.length === 0 && <div className="sp-empty">No guards yet.</div>}
        {guards.map(g => (
          <div key={g.guard_id} className={`sp-guard-row${g.is_active ? "" : " sp-guard-row--inactive"}`}>
            <div className="sp-guard-info">
              <span className="sp-guard-name">{g.name || "—"}</span>
              <span className="sp-guard-meta">
                {g.phone ? g.phone : ""}
                {g.phone && " · "}
                <span className={`sp-role-pill sp-role-pill--${g.role?.toLowerCase()}`}>{g.role}</span>
                {g.on_duty_now && <span className="sp-on-duty">● On duty</span>}
              </span>
            </div>
            <button
              className={`sp-btn sp-btn--sm ${g.is_active ? "sp-btn--ghost-red" : "sp-btn--ghost"}`}
              disabled={busy === g.guard_id}
              onClick={() => toggleActive(g)}
            >
              {busy === g.guard_id ? "…" : g.is_active ? <><UserX size={13} /> Deactivate</> : <><UserCheck size={13} /> Activate</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Shifts tab ────────────────────────────────────────────────────────────────
function ShiftsTab() {
  const [guards, setGuards] = useState([]);
  const [date, setDate] = useState(todayKey());
  const [shifts, setShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ guard_id: "", start_time: "08:00", end_time: "16:00", assigned_area: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    securityApi.listGuards({ active: true }).then(data => {
      setGuards(Array.isArray(data) ? data : data.guards || []);
    }).catch(() => {});
  }, []);

  const loadShifts = useCallback(async (d) => {
    setLoadingShifts(true);
    try {
      const data = await securityApi.listShifts({ date: d });
      setShifts(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load shifts");
    } finally {
      setLoadingShifts(false);
    }
  }, []);

  useEffect(() => { loadShifts(date); }, [date, loadShifts]);

  const guardName = (gid) => guards.find(g => g.guard_id === gid)?.name || gid;

  const deleteShift = async (s) => {
    setDeleting(s.id);
    try {
      await securityApi.deleteShift(s.id);
      toast.success("Shift removed");
      await loadShifts(date);
    } catch (e) {
      toast.error(e?.message || "Could not delete shift");
    } finally {
      setDeleting(null);
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!form.guard_id) { toast.error("Select a guard"); return; }
    setSaving(true);
    try {
      const starts_at = new Date(`${date}T${form.start_time}:00`).toISOString();
      const ends_at = new Date(`${date}T${form.end_time}:00`).toISOString();
      await securityApi.createShift({
        guard_id: form.guard_id,
        starts_at,
        ends_at,
        assigned_area: form.assigned_area.trim() || null,
      });
      toast.success("Shift added");
      setAddOpen(false);
      await loadShifts(date);
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not add shift");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="sp-row-between sp-mb">
        <input
          type="date"
          className="sp-date-input"
          value={date}
          onChange={e => e.target.value && setDate(e.target.value)}
        />
        <button className="sp-btn sp-btn--primary" onClick={() => setAddOpen(true)}>
          <Plus size={14} /> Add shift
        </button>
      </div>

      {addOpen && (
        <form className="sp-form-card" onSubmit={submitAdd}>
          <div className="sp-form-head">
            New shift for {date}
            <button type="button" className="sp-form-close" onClick={() => setAddOpen(false)}><X size={16} /></button>
          </div>
          <div className="sp-field">
            <label>Guard</label>
            <select value={form.guard_id} onChange={e => setForm(p => ({ ...p, guard_id: e.target.value }))} required>
              <option value="">Select guard…</option>
              {guards.map(g => <option key={g.guard_id} value={g.guard_id}>{g.name}</option>)}
            </select>
          </div>
          <div className="sp-field-row">
            <div className="sp-field">
              <label>Start</label>
              <input type="time" value={form.start_time} onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))} required />
            </div>
            <div className="sp-field">
              <label>End</label>
              <input type="time" value={form.end_time} onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))} required />
            </div>
          </div>
          <div className="sp-field">
            <label>Area (optional)</label>
            <input value={form.assigned_area} onChange={e => setForm(p => ({ ...p, assigned_area: e.target.value }))} placeholder="Main gate, North wing…" />
          </div>
          <div className="sp-form-actions">
            <button type="button" className="sp-btn sp-btn--ghost" onClick={() => setAddOpen(false)}>Cancel</button>
            <button type="submit" className="sp-btn sp-btn--primary" disabled={saving}>{saving ? "Saving…" : "Add shift"}</button>
          </div>
        </form>
      )}

      {loadingShifts ? (
        <div className="sp-empty">Loading shifts…</div>
      ) : shifts.length === 0 ? (
        <div className="sp-empty">No shifts on {date}.</div>
      ) : (
        <div className="sp-list">
          {shifts.map(s => (
            <div key={s.id} className="sp-shift-row">
              <Clock size={14} className="sp-shift-icon" />
              <div className="sp-shift-info">
                <span className="sp-guard-name">{guardName(s.guard_id)}</span>
                <span className="sp-guard-meta">{fmtTime(s.starts_at)} – {fmtTime(s.ends_at)}{s.assigned_area ? ` · ${s.assigned_area}` : ""}</span>
              </div>
              <button
                className="sp-btn sp-btn--sm sp-btn--ghost-red"
                disabled={deleting === s.id}
                onClick={() => deleteShift(s)}
              >
                {deleting === s.id ? "…" : <Trash2 size={13} />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Patrols tab ───────────────────────────────────────────────────────────────
function PatrolsTab() {
  const [view, setView] = useState("checkpoints"); // "checkpoints" | "routes"
  const [checkpoints, setCheckpoints] = useState([]);
  const [loadingCp, setLoadingCp] = useState(true);
  const [cpBusy, setCpBusy] = useState(null);
  const [addCpOpen, setAddCpOpen] = useState(false);
  const [cpForm, setCpForm] = useState({ label: "", lat: "", lng: "" });
  const [savingCp, setSavingCp] = useState(false);
  const [summaryDate, setSummaryDate] = useState(todayKey());
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // ── Routes management state ──
  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [routesLoaded, setRoutesLoaded] = useState(false);
  const [addRouteOpen, setAddRouteOpen] = useState(false);
  const [routeForm, setRouteForm] = useState({ name: "", description: "" });
  const [savingRoute, setSavingRoute] = useState(false);
  const [routeBusy, setRouteBusy] = useState(null);
  // Inline expansion: { routeId, mode: "cps" | "assign" } | null
  const [expanded, setExpanded] = useState(null);
  const [routeCps, setRouteCps] = useState([]);
  const [loadingRouteCps, setLoadingRouteCps] = useState(false);
  const [cpActionBusy, setCpActionBusy] = useState(null);
  const [guards, setGuards] = useState([]);
  const [assignForm, setAssignForm] = useState({ guard_id: "", date: todayKey() });
  const [savingAssign, setSavingAssign] = useState(false);

  const loadCheckpoints = useCallback(async () => {
    setLoadingCp(true);
    try {
      const data = await securityApi.listCheckpoints({});
      setCheckpoints(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load checkpoints");
    } finally {
      setLoadingCp(false);
    }
  }, []);

  const loadSummary = useCallback(async (d) => {
    setLoadingSummary(true);
    try {
      const data = await securityApi.getPatrolSummary({ date: d });
      setSummary(data);
    } catch (_) {
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  useEffect(() => { loadCheckpoints(); }, [loadCheckpoints]);
  useEffect(() => { loadSummary(summaryDate); }, [summaryDate, loadSummary]);

  const toggleCp = async (cp) => {
    setCpBusy(cp.id);
    try {
      await securityApi.patchCheckpoint(cp.id, { is_active: !cp.is_active });
      await loadCheckpoints();
    } catch (e) {
      toast.error(e?.message || "Could not update checkpoint");
    } finally {
      setCpBusy(null);
    }
  };

  const submitAddCp = async (e) => {
    e.preventDefault();
    if (!cpForm.label.trim()) return;
    setSavingCp(true);
    try {
      const body = { label: cpForm.label.trim() };
      if (cpForm.lat) body.lat = parseFloat(cpForm.lat);
      if (cpForm.lng) body.lng = parseFloat(cpForm.lng);
      await securityApi.createCheckpoint(body);
      toast.success("Checkpoint created");
      setAddCpOpen(false);
      setCpForm({ label: "", lat: "", lng: "" });
      await loadCheckpoints();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not create checkpoint");
    } finally {
      setSavingCp(false);
    }
  };

  // ── Routes ──
  const loadRoutes = useCallback(async () => {
    setLoadingRoutes(true);
    try {
      const data = await securityApi.listRoutes({ active: true });
      setRoutes(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load routes");
    } finally {
      setLoadingRoutes(false);
      setRoutesLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (view === "routes" && !routesLoaded) loadRoutes();
  }, [view, routesLoaded, loadRoutes]);

  // Active guards for the assign dropdown (loaded lazily on first assign open).
  const ensureGuards = useCallback(async () => {
    if (guards.length > 0) return;
    try {
      const data = await securityApi.listGuards({ active: true });
      setGuards(Array.isArray(data) ? data : []);
    } catch (_) {
      toast.error("Could not load guards");
    }
  }, [guards.length]);

  const loadRouteCps = useCallback(async (routeId) => {
    setLoadingRouteCps(true);
    try {
      const data = await securityApi.listRouteCheckpoints(routeId);
      setRouteCps(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load route checkpoints");
      setRouteCps([]);
    } finally {
      setLoadingRouteCps(false);
    }
  }, []);

  const openCps = (route) => {
    if (expanded && expanded.routeId === route.id && expanded.mode === "cps") {
      setExpanded(null);
      return;
    }
    setExpanded({ routeId: route.id, mode: "cps" });
    setRouteCps([]);
    loadRouteCps(route.id);
  };

  const openAssign = (route) => {
    if (expanded && expanded.routeId === route.id && expanded.mode === "assign") {
      setExpanded(null);
      return;
    }
    setExpanded({ routeId: route.id, mode: "assign" });
    setAssignForm({ guard_id: "", date: todayKey() });
    ensureGuards();
  };

  const submitAddRoute = async (e) => {
    e.preventDefault();
    if (!routeForm.name.trim()) return;
    setSavingRoute(true);
    try {
      const body = { name: routeForm.name.trim() };
      if (routeForm.description.trim()) body.description = routeForm.description.trim();
      await securityApi.createRoute(body);
      toast.success("Route created");
      setAddRouteOpen(false);
      setRouteForm({ name: "", description: "" });
      await loadRoutes();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not create route");
    } finally {
      setSavingRoute(false);
    }
  };

  const deactivateRoute = async (route) => {
    setRouteBusy(route.id);
    try {
      await securityApi.deleteRoute(route.id);
      toast.success("Route deactivated");
      if (expanded && expanded.routeId === route.id) setExpanded(null);
      await loadRoutes();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not deactivate route");
    } finally {
      setRouteBusy(null);
    }
  };

  const addCpToRoute = async (routeId, checkpointId, orderIndex) => {
    setCpActionBusy(checkpointId);
    try {
      await securityApi.addRouteCheckpoint(routeId, { checkpoint_id: checkpointId, order_index: orderIndex });
      toast.success("Checkpoint added to route");
      await loadRouteCps(routeId);
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not add checkpoint");
    } finally {
      setCpActionBusy(null);
    }
  };

  const removeCpFromRoute = async (routeId, checkpointId) => {
    setCpActionBusy(checkpointId);
    try {
      await securityApi.removeRouteCheckpoint(routeId, checkpointId);
      toast.success("Checkpoint removed");
      await loadRouteCps(routeId);
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not remove checkpoint");
    } finally {
      setCpActionBusy(null);
    }
  };

  const submitAssign = async (routeId, e) => {
    e.preventDefault();
    if (!assignForm.guard_id) { toast.error("Select a guard"); return; }
    setSavingAssign(true);
    try {
      const body = { guard_id: assignForm.guard_id };
      if (assignForm.date) body.assigned_date = assignForm.date;
      const res = await securityApi.assignRoute(routeId, body);
      toast.success(`Route assigned to ${res?.guard_name || "guard"}`);
      setExpanded(null);
      setAssignForm({ guard_id: "", date: todayKey() });
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not assign route");
    } finally {
      setSavingAssign(false);
    }
  };

  return (
    <div>
      {/* Sub-view toggle: Checkpoints | Routes */}
      <div className="sp-seg sp-mb-lg">
        <button
          type="button"
          className={`sp-btn sp-btn--sm ${view === "checkpoints" ? "sp-btn--primary" : "sp-btn--ghost"}`}
          onClick={() => setView("checkpoints")}
        >
          <MapPin size={13} /> Checkpoints
        </button>
        <button
          type="button"
          className={`sp-btn sp-btn--sm ${view === "routes" ? "sp-btn--primary" : "sp-btn--ghost"}`}
          onClick={() => setView("routes")}
        >
          <RouteIcon size={13} /> Routes
        </button>
      </div>

      {view === "checkpoints" && (
      <>
      {/* Checkpoint list */}
      <div className="sp-section-label">Checkpoints</div>
      <div className="sp-row-between sp-mb">
        <span className="sp-count">{checkpoints.length} checkpoint{checkpoints.length !== 1 ? "s" : ""}</span>
        <button className="sp-btn sp-btn--primary" onClick={() => setAddCpOpen(true)}>
          <Plus size={14} /> Add checkpoint
        </button>
      </div>

      {addCpOpen && (
        <form className="sp-form-card" onSubmit={submitAddCp}>
          <div className="sp-form-head">
            New checkpoint
            <button type="button" className="sp-form-close" onClick={() => setAddCpOpen(false)}><X size={16} /></button>
          </div>
          <div className="sp-field">
            <label>Label</label>
            <input value={cpForm.label} onChange={e => setCpForm(p => ({ ...p, label: e.target.value }))} placeholder="Main gate, Parking A…" required />
          </div>
          <div className="sp-field-row">
            <div className="sp-field">
              <label>Lat (optional)</label>
              <input type="number" step="any" value={cpForm.lat} onChange={e => setCpForm(p => ({ ...p, lat: e.target.value }))} placeholder="12.9716" />
            </div>
            <div className="sp-field">
              <label>Lng (optional)</label>
              <input type="number" step="any" value={cpForm.lng} onChange={e => setCpForm(p => ({ ...p, lng: e.target.value }))} placeholder="77.5946" />
            </div>
          </div>
          <div className="sp-form-actions">
            <button type="button" className="sp-btn sp-btn--ghost" onClick={() => setAddCpOpen(false)}>Cancel</button>
            <button type="submit" className="sp-btn sp-btn--primary" disabled={savingCp}>{savingCp ? "Creating…" : "Create"}</button>
          </div>
        </form>
      )}

      {loadingCp ? (
        <div className="sp-empty">Loading…</div>
      ) : checkpoints.length === 0 ? (
        <div className="sp-empty">No checkpoints yet.</div>
      ) : (
        <div className="sp-list sp-mb-lg">
          {checkpoints.map(cp => (
            <div key={cp.id} className={`sp-cp-row${cp.is_active ? "" : " sp-cp-row--inactive"}`}>
              <MapPin size={14} className="sp-shift-icon" />
              <div className="sp-shift-info">
                <span className="sp-guard-name">{cp.label}</span>
                {(cp.lat || cp.lng) && (
                  <span className="sp-guard-meta">{cp.lat?.toFixed(4)}, {cp.lng?.toFixed(4)}</span>
                )}
              </div>
              <button
                className={`sp-btn sp-btn--sm ${cp.is_active ? "sp-btn--ghost-red" : "sp-btn--ghost"}`}
                disabled={cpBusy === cp.id}
                onClick={() => toggleCp(cp)}
              >
                {cpBusy === cp.id ? "…" : cp.is_active ? "Deactivate" : "Activate"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Patrol summary */}
      <div className="sp-section-label">Patrol summary</div>
      <div className="sp-row-between sp-mb">
        <input
          type="date"
          className="sp-date-input"
          value={summaryDate}
          onChange={e => e.target.value && setSummaryDate(e.target.value)}
        />
      </div>
      {loadingSummary ? (
        <div className="sp-empty">Loading summary…</div>
      ) : !summary || summary.guards?.length === 0 ? (
        <div className="sp-empty">No patrol data for {summaryDate}.</div>
      ) : (
        <div className="sp-summary-table-wrap">
          <table className="sp-summary-table">
            <thead>
              <tr><th>Guard</th><th>Done</th><th>Missed</th><th>Pending</th></tr>
            </thead>
            <tbody>
              {summary.guards.map(r => (
                <tr key={r.guard_id}>
                  <td>{r.name || "—"}</td>
                  <td className="sp-cell-green">{r.completed}</td>
                  <td className={r.missed > 0 ? "sp-cell-red" : ""}>{r.missed}</td>
                  <td className="sp-cell-muted">{r.pending}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {view === "routes" && (
      <>
      <div className="sp-section-label">Patrol routes</div>
      <div className="sp-row-between sp-mb">
        <span className="sp-count">{routes.length} route{routes.length !== 1 ? "s" : ""}</span>
        <button className="sp-btn sp-btn--primary" onClick={() => setAddRouteOpen(true)}>
          <Plus size={14} /> New route
        </button>
      </div>

      {addRouteOpen && (
        <form className="sp-form-card" onSubmit={submitAddRoute}>
          <div className="sp-form-head">
            New patrol route
            <button type="button" className="sp-form-close" onClick={() => setAddRouteOpen(false)}><X size={16} /></button>
          </div>
          <div className="sp-field">
            <label>Route name</label>
            <input value={routeForm.name} onChange={e => setRouteForm(p => ({ ...p, name: e.target.value }))} placeholder="Night round A" required />
          </div>
          <div className="sp-field">
            <label>Description (optional)</label>
            <input value={routeForm.description} onChange={e => setRouteForm(p => ({ ...p, description: e.target.value }))} placeholder="Main gate → Pool → Club" />
          </div>
          <div className="sp-form-actions">
            <button type="button" className="sp-btn sp-btn--ghost" onClick={() => setAddRouteOpen(false)}>Cancel</button>
            <button type="submit" className="sp-btn sp-btn--primary" disabled={savingRoute}>{savingRoute ? "Creating…" : "Create"}</button>
          </div>
        </form>
      )}

      {loadingRoutes ? (
        <div className="sp-empty">Loading…</div>
      ) : routes.length === 0 ? (
        <div className="sp-empty">No routes yet. Create a route, add checkpoints, then assign it to a guard for a date.</div>
      ) : (
        <div className="sp-list">
          {routes.map(route => {
            const isCps = expanded && expanded.routeId === route.id && expanded.mode === "cps";
            const isAssign = expanded && expanded.routeId === route.id && expanded.mode === "assign";
            const onRouteIds = new Set(routeCps.map(rc => rc.checkpoint_id));
            const addable = checkpoints.filter(cp => cp.is_active && !onRouteIds.has(cp.id));
            return (
              <div key={route.id} className="sp-route-card">
                <div className="sp-route-head">
                  <RouteIcon size={15} className="sp-shift-icon" />
                  <div className="sp-guard-info">
                    <span className="sp-guard-name">{route.name}</span>
                    {route.description && <span className="sp-guard-meta">{route.description}</span>}
                  </div>
                  <button className="sp-btn sp-btn--sm sp-btn--ghost" onClick={() => openCps(route)}>
                    <MapPin size={13} /> Checkpoints
                  </button>
                  <button className="sp-btn sp-btn--sm sp-btn--ghost" onClick={() => openAssign(route)}>
                    <UserCheck size={13} /> Assign
                  </button>
                  <button
                    className="sp-btn sp-btn--sm sp-btn--ghost-red"
                    disabled={routeBusy === route.id}
                    onClick={() => deactivateRoute(route)}
                  >
                    {routeBusy === route.id ? "…" : <><Trash2 size={13} /> Deactivate</>}
                  </button>
                </div>

                {isCps && (
                  <div className="sp-route-panel">
                    {loadingRouteCps ? (
                      <div className="sp-empty">Loading…</div>
                    ) : (
                      <>
                        {routeCps.length === 0 ? (
                          <div className="sp-route-hint">No checkpoints on this route yet.</div>
                        ) : (
                          <div className="sp-list sp-mb">
                            {routeCps.map(rc => (
                              <div key={rc.id} className="sp-cp-row">
                                <span className="sp-order-badge">{rc.order_index + 1}</span>
                                <div className="sp-shift-info">
                                  <span className="sp-guard-name">{rc.checkpoint_label || "Unknown"}</span>
                                </div>
                                <button
                                  className="sp-btn sp-btn--sm sp-btn--ghost-red"
                                  disabled={cpActionBusy === rc.checkpoint_id}
                                  onClick={() => removeCpFromRoute(route.id, rc.checkpoint_id)}
                                >
                                  {cpActionBusy === rc.checkpoint_id ? "…" : <><Minus size={13} /> Remove</>}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="sp-section-label">Add checkpoint</div>
                        {addable.length === 0 ? (
                          <div className="sp-route-hint">All active checkpoints are already on this route.</div>
                        ) : (
                          <div className="sp-list">
                            {addable.map((cp, idx) => (
                              <button
                                key={cp.id}
                                type="button"
                                className="sp-cp-add-row"
                                disabled={cpActionBusy === cp.id}
                                onClick={() => addCpToRoute(route.id, cp.id, routeCps.length + idx)}
                              >
                                <Plus size={14} className="sp-shift-icon" />
                                <span className="sp-guard-name">{cp.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {isAssign && (
                  <div className="sp-route-panel">
                    <form onSubmit={(e) => submitAssign(route.id, e)}>
                      <div className="sp-field">
                        <label>Date</label>
                        <input
                          type="date"
                          value={assignForm.date}
                          onChange={e => setAssignForm(p => ({ ...p, date: e.target.value }))}
                        />
                      </div>
                      <div className="sp-field">
                        <label>Guard</label>
                        <select value={assignForm.guard_id} onChange={e => setAssignForm(p => ({ ...p, guard_id: e.target.value }))} required>
                          <option value="">Select a guard…</option>
                          {guards.map(g => (
                            <option key={g.guard_id} value={g.guard_id}>
                              {g.name || "Unnamed guard"}{g.on_duty_now ? " · On duty" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sp-form-actions">
                        <button type="button" className="sp-btn sp-btn--ghost" onClick={() => setExpanded(null)}>Cancel</button>
                        <button type="submit" className="sp-btn sp-btn--primary" disabled={savingAssign || !assignForm.guard_id}>
                          {savingAssign ? "Assigning…" : "Assign"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
}

// ── Compliance tab ──────────────────────────────────────────────────────────
// Supervisor view of a day's payroll eligibility. Mirrors the mobile
// `(security)/compliance.tsx` flow: pick a date, see "{eligible}/{total}
// payroll-eligible", one row per assignment with an Eligible/Blocked pill and
// failed checks, expand a row to load the per-assignment checklist, and
// Approve / Reject a PENDING shift report inline.
function ComplianceTab() {
  const [date, setDate] = useState(todayKey());
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openId, setOpenId] = useState(null); // assignment_id of expanded row
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(null); // "approve" | "reject"

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(false);
    try {
      const data = await securityApi.getDayCompliance({ date: d });
      setDay(data);
    } catch (e) {
      setDay(null);
      setError(true);
      toast.error(e?.message || "Could not load compliance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const openRow = async (row) => {
    if (openId === row.assignment_id) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(row.assignment_id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const data = await securityApi.getAssignmentCompliance(row.assignment_id);
      setDetail(data);
    } catch (e) {
      toast.error(e?.message || "Could not load checklist");
    } finally {
      setDetailLoading(false);
    }
  };

  const review = async (row, action) => {
    if (!row.report_id) return;
    setReviewBusy(action);
    try {
      if (action === "approve") {
        await securityApi.approveReport(row.report_id);
        toast.success("Report approved");
      } else {
        await securityApi.rejectReport(row.report_id);
        toast.success("Report rejected");
      }
      setOpenId(null);
      setDetail(null);
      await load(date);
    } catch (e) {
      toast.error(e?.message || "Could not update the report");
    } finally {
      setReviewBusy(null);
    }
  };

  const rows = day?.rows || [];

  return (
    <div>
      <div className="sp-row-between sp-mb">
        <input
          type="date"
          className="sp-date-input"
          value={date}
          onChange={e => e.target.value && setDate(e.target.value)}
        />
        {date !== todayKey() && (
          <button className="sp-btn sp-btn--ghost sp-btn--sm" onClick={() => setDate(todayKey())}>Today</button>
        )}
      </div>

      {/* Summary */}
      <div className="sp-comp-summary">
        <span className="sp-comp-summary-num">{day?.payroll_eligible ?? 0}/{day?.total ?? 0}</span>
        <span className="sp-comp-summary-label">payroll-eligible</span>
      </div>

      {loading ? (
        <div className="sp-empty">Loading compliance…</div>
      ) : error ? (
        <div className="sp-empty">Could not load compliance for {date}.</div>
      ) : rows.length === 0 ? (
        <div className="sp-empty">No guards were on duty on {date}.</div>
      ) : (
        <div className="sp-list">
          {rows.map(row => {
            const isOpen = openId === row.assignment_id;
            const isPending = row.report_status === "PENDING" && row.report_id;
            return (
              <div key={row.assignment_id} className="sp-comp-card">
                <button className="sp-comp-row" onClick={() => openRow(row)}>
                  <div className="sp-guard-info">
                    <span className="sp-guard-name">{row.guard_name || "—"}</span>
                    <span className="sp-guard-meta">
                      {row.shift === "DAY" ? "Day shift" : row.shift === "NIGHT" ? "Night shift" : row.shift}
                      {!row.payroll_eligible && row.failed_checks?.length > 0 && (
                        <span className="sp-comp-failed">{row.failed_checks.join(" · ")}</span>
                      )}
                    </span>
                  </div>
                  <span className={`sp-pill ${row.payroll_eligible ? "sp-pill--success" : "sp-pill--danger"}`}>
                    {row.payroll_eligible ? "Eligible" : "Blocked"}
                  </span>
                  {isPending && <span className="sp-comp-review">Review</span>}
                  {isOpen ? <ChevronDown size={16} className="sp-shift-icon" /> : <ChevronRight size={16} className="sp-shift-icon" />}
                </button>

                {isOpen && (
                  <div className="sp-comp-detail">
                    {detailLoading ? (
                      <div className="sp-comp-detail-msg">Loading checklist…</div>
                    ) : !detail || (detail.checks || []).length === 0 ? (
                      <div className="sp-comp-detail-msg">No checks recorded.</div>
                    ) : (
                      <div>
                        {detail.checks.map(check => (
                          <div key={check.key} className="sp-comp-check">
                            <div className="sp-guard-info">
                              <span className="sp-comp-check-label">{check.label}</span>
                              {check.detail && <span className="sp-guard-meta">{check.detail}</span>}
                            </div>
                            <span className={`sp-pill sp-pill--${check.status === "PASS" ? "success" : check.status === "FAIL" ? "danger" : "neutral"}`}>
                              {check.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {isPending && (
                      <div className="sp-form-actions sp-comp-actions">
                        <button
                          className="sp-btn sp-btn--ghost-red"
                          disabled={reviewBusy !== null}
                          onClick={() => review(row, "reject")}
                        >
                          {reviewBusy === "reject" ? "Rejecting…" : "Reject"}
                        </button>
                        <button
                          className="sp-btn sp-btn--primary"
                          disabled={reviewBusy !== null}
                          onClick={() => review(row, "approve")}
                        >
                          {reviewBusy === "approve" ? "Approving…" : "Approve"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Handover tab ──────────────────────────────────────────────────────────────
// Supervisor view of digital shift handovers for a day. Mirrors the mobile
// `(security)/handover.tsx` flow: pick a date, list handovers (status pill,
// from→to shift, author and the populated note sections), create a new handover
// via a form that "Create & submit"s in one go, and Acknowledge a SUBMITTED
// handover. A lingering DRAFT can be Submitted from its row. Both admin and
// incharge reach this (a SECURITY_INCHARGE is a SUPERVISOR guard); the backend
// `/api/handover` endpoints all require_supervisor.
const HANDOVER_SECTIONS = [
  { key: "open_incidents", label: "Open incidents" },
  { key: "pending_patrols", label: "Pending patrols" },
  { key: "equipment_notes", label: "Equipment notes" },
  { key: "keys_notes", label: "Keys notes" },
  { key: "visitors_notes", label: "Visitors notes" },
  { key: "general_notes", label: "General notes" },
];

const EMPTY_HANDOVER_NOTES = {
  open_incidents: "",
  pending_patrols: "",
  equipment_notes: "",
  keys_notes: "",
  visitors_notes: "",
  general_notes: "",
};

function shiftLabel(s) {
  return s === "DAY" ? "Day" : s === "NIGHT" ? "Night" : s;
}

function HandoverTab() {
  const [date, setDate] = useState(todayKey());
  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [fromShift, setFromShift] = useState("DAY");
  const [toShift, setToShift] = useState("NIGHT");
  const [notes, setNotes] = useState(EMPTY_HANDOVER_NOTES);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null); // handover id being submitted/acknowledged

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(false);
    try {
      const data = await securityApi.listHandovers({ date: d });
      setHandovers(Array.isArray(data) ? data : []);
    } catch (e) {
      setHandovers([]);
      setError(true);
      toast.error(e?.message || "Could not load handovers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const resetForm = () => {
    setFromShift("DAY");
    setToShift("NIGHT");
    setNotes(EMPTY_HANDOVER_NOTES);
  };

  const submitNew = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const created = await securityApi.createHandover({
        handover_date: date,
        from_shift: fromShift,
        to_shift: toShift,
        open_incidents: notes.open_incidents.trim() || null,
        pending_patrols: notes.pending_patrols.trim() || null,
        equipment_notes: notes.equipment_notes.trim() || null,
        keys_notes: notes.keys_notes.trim() || null,
        visitors_notes: notes.visitors_notes.trim() || null,
        general_notes: notes.general_notes.trim() || null,
      });
      await securityApi.submitHandover(created.id);
      toast.success("Handover submitted");
      setAddOpen(false);
      resetForm();
      await load(date);
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not submit handover");
    } finally {
      setSaving(false);
    }
  };

  const submitDraft = async (h) => {
    setBusy(h.id);
    try {
      await securityApi.submitHandover(h.id);
      toast.success("Handover submitted");
      await load(date);
    } catch (e) {
      toast.error(e?.message || "Could not submit handover");
    } finally {
      setBusy(null);
    }
  };

  const acknowledge = async (h) => {
    setBusy(h.id);
    try {
      await securityApi.acknowledgeHandover(h.id);
      toast.success("Handover acknowledged");
      await load(date);
    } catch (e) {
      toast.error(e?.message || "Could not acknowledge handover");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="sp-row-between sp-mb">
        <div className="sp-row-between" style={{ gap: 8, justifyContent: "flex-start" }}>
          <input
            type="date"
            className="sp-date-input"
            value={date}
            onChange={e => e.target.value && setDate(e.target.value)}
          />
          {date !== todayKey() && (
            <button className="sp-btn sp-btn--ghost sp-btn--sm" onClick={() => setDate(todayKey())}>Today</button>
          )}
        </div>
        <button className="sp-btn sp-btn--primary" onClick={() => setAddOpen(true)}>
          <Plus size={14} /> New handover
        </button>
      </div>

      {addOpen && (
        <form className="sp-form-card" onSubmit={submitNew}>
          <div className="sp-form-head">
            New handover for {date}
            <button type="button" className="sp-form-close" onClick={() => { setAddOpen(false); resetForm(); }}><X size={16} /></button>
          </div>
          <div className="sp-field-row">
            <div className="sp-field">
              <label>From shift</label>
              <select value={fromShift} onChange={e => setFromShift(e.target.value)}>
                <option value="DAY">Day</option>
                <option value="NIGHT">Night</option>
              </select>
            </div>
            <div className="sp-field">
              <label>To shift</label>
              <select value={toShift} onChange={e => setToShift(e.target.value)}>
                <option value="DAY">Day</option>
                <option value="NIGHT">Night</option>
              </select>
            </div>
          </div>
          {HANDOVER_SECTIONS.map(sec => (
            <div key={sec.key} className="sp-field">
              <label>{sec.label}</label>
              <textarea
                rows={2}
                value={notes[sec.key]}
                onChange={e => setNotes(p => ({ ...p, [sec.key]: e.target.value }))}
                placeholder={`Add ${sec.label.toLowerCase()}…`}
                maxLength={4000}
              />
            </div>
          ))}
          <div className="sp-form-actions">
            <button type="button" className="sp-btn sp-btn--ghost" onClick={() => { setAddOpen(false); resetForm(); }}>Cancel</button>
            <button type="submit" className="sp-btn sp-btn--primary" disabled={saving}>{saving ? "Submitting…" : "Create & submit"}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="sp-empty">Loading handovers…</div>
      ) : error ? (
        <div className="sp-empty">Could not load handovers for {date}.</div>
      ) : handovers.length === 0 ? (
        <div className="sp-empty">No handovers on {date}.</div>
      ) : (
        <div className="sp-list">
          {handovers.map(h => (
            <div key={h.id} className="sp-comp-card">
              <div className="sp-handover-head">
                <div className="sp-guard-info">
                  <span className="sp-guard-name">{shiftLabel(h.from_shift)} → {shiftLabel(h.to_shift)}</span>
                  <span className="sp-guard-meta">
                    From {h.from_user_name || "—"}{h.to_user_name ? ` · To ${h.to_user_name}` : ""}
                  </span>
                </div>
                <span className={`sp-pill ${h.status === "ACKNOWLEDGED" ? "sp-pill--success" : "sp-pill--neutral"}`}>
                  {h.status}
                </span>
              </div>

              <div className="sp-handover-body">
                {HANDOVER_SECTIONS.every(sec => !h[sec.key]) ? (
                  <div className="sp-comp-detail-msg">No notes recorded.</div>
                ) : (
                  HANDOVER_SECTIONS.map(sec => {
                    if (!h[sec.key]) return null;
                    return (
                      <div key={sec.key} className="sp-handover-note">
                        <span className="sp-handover-note-label">{sec.label}</span>
                        <span className="sp-handover-note-text">{h[sec.key]}</span>
                      </div>
                    );
                  })
                )}

                {(h.status === "DRAFT" || h.status === "SUBMITTED") && (
                  <div className="sp-form-actions sp-comp-actions">
                    {h.status === "DRAFT" && (
                      <button className="sp-btn sp-btn--primary" disabled={busy === h.id} onClick={() => submitDraft(h)}>
                        {busy === h.id ? "Submitting…" : "Submit"}
                      </button>
                    )}
                    {h.status === "SUBMITTED" && (
                      <button className="sp-btn sp-btn--primary" disabled={busy === h.id} onClick={() => acknowledge(h)}>
                        {busy === h.id ? "Acknowledging…" : "Acknowledge"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Incidents tab ─────────────────────────────────────────────────────────────
// Security-staff incident log. Mirrors the mobile `(security)/incidents.tsx`
// flow against the same /api/incidents endpoints (require_security_staff, so
// both admin and incharge qualify): filter by status, log a new incident
// (title/category/severity/description) and resolve an OPEN one with an
// optional resolution note. Matches the mobile field set exactly — no more,
// no less.
const INCIDENT_STATUS_FILTERS = [
  { key: "ALL", label: "All" },
  { key: "OPEN", label: "Open" },
  { key: "RESOLVED", label: "Resolved" },
];

const INCIDENT_CATEGORIES = [
  "INTRUSION",
  "THEFT",
  "FIRE",
  "MEDICAL",
  "DISPUTE",
  "MAINTENANCE",
  "OTHER",
];

const INCIDENT_SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

function titleCase(s) {
  if (!s) return "";
  return s.charAt(0) + s.slice(1).toLowerCase();
}

function severityPillClass(s) {
  return s === "CRITICAL" || s === "HIGH" ? "sp-pill--danger" : "sp-pill--neutral";
}

function IncidentsTab() {
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "OTHER", severity: "MEDIUM", description: "" });
  const [saving, setSaving] = useState(false);
  const [resolveId, setResolveId] = useState(null); // incident being resolved
  const [resolutionNote, setResolutionNote] = useState("");
  const [resolving, setResolving] = useState(false);

  const load = useCallback(async (filter) => {
    setLoading(true);
    setError(false);
    try {
      const status = filter === "ALL" ? undefined : filter;
      const data = await securityApi.listIncidents({ status });
      setIncidents(Array.isArray(data) ? data : []);
    } catch (e) {
      setIncidents([]);
      setError(true);
      toast.error(e?.message || "Could not load incidents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(statusFilter); }, [statusFilter, load]);

  const resetForm = () => setForm({ title: "", category: "OTHER", severity: "MEDIUM", description: "" });

  const submitLog = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await securityApi.logIncident({
        title: form.title.trim(),
        category: form.category,
        severity: form.severity,
        description: form.description.trim() || null,
        occurred_at: new Date().toISOString(),
      });
      toast.success("Incident logged");
      setAddOpen(false);
      resetForm();
      await load(statusFilter);
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not log incident");
    } finally {
      setSaving(false);
    }
  };

  const submitResolve = async (inc) => {
    setResolving(true);
    try {
      await securityApi.resolveIncident(inc.id, { resolution_note: resolutionNote.trim() || null });
      toast.success("Incident resolved");
      setResolveId(null);
      setResolutionNote("");
      await load(statusFilter);
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not resolve incident");
    } finally {
      setResolving(false);
    }
  };

  return (
    <div>
      <div className="sp-row-between sp-mb">
        <div className="sp-row-between" style={{ gap: 4, justifyContent: "flex-start" }}>
          {INCIDENT_STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              className={`sp-btn sp-btn--sm ${statusFilter === f.key ? "sp-btn--primary" : "sp-btn--ghost"}`}
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button className="sp-btn sp-btn--primary" onClick={() => setAddOpen(true)}>
          <Plus size={14} /> Log incident
        </button>
      </div>

      {addOpen && (
        <form className="sp-form-card" onSubmit={submitLog}>
          <div className="sp-form-head">
            Log incident
            <button type="button" className="sp-form-close" onClick={() => { setAddOpen(false); resetForm(); }}><X size={16} /></button>
          </div>
          <div className="sp-field">
            <label>Title</label>
            <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="What happened?" maxLength={160} required />
          </div>
          <div className="sp-field-row">
            <div className="sp-field">
              <label>Category</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {INCIDENT_CATEGORIES.map(c => <option key={c} value={c}>{titleCase(c)}</option>)}
              </select>
            </div>
            <div className="sp-field">
              <label>Severity</label>
              <select value={form.severity} onChange={e => setForm(p => ({ ...p, severity: e.target.value }))}>
                {INCIDENT_SEVERITIES.map(s => <option key={s} value={s}>{titleCase(s)}</option>)}
              </select>
            </div>
          </div>
          <div className="sp-field">
            <label>Description (optional)</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Add any details…"
              maxLength={4000}
            />
          </div>
          <div className="sp-form-actions">
            <button type="button" className="sp-btn sp-btn--ghost" onClick={() => { setAddOpen(false); resetForm(); }}>Cancel</button>
            <button type="submit" className="sp-btn sp-btn--primary" disabled={saving}>{saving ? "Logging…" : "Log incident"}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="sp-empty">Loading incidents…</div>
      ) : error ? (
        <div className="sp-empty">Could not load incidents.</div>
      ) : incidents.length === 0 ? (
        <div className="sp-empty">No incidents.</div>
      ) : (
        <div className="sp-list">
          {incidents.map(inc => (
            <div key={inc.id} className="sp-comp-card">
              <div className="sp-handover-head">
                <div className="sp-guard-info">
                  <span className="sp-guard-name">{inc.title}</span>
                  <span className="sp-guard-meta">
                    <span className={`sp-pill ${severityPillClass(inc.severity)}`}>{titleCase(inc.severity)}</span>
                    <span className="sp-pill sp-pill--neutral">{titleCase(inc.category)}</span>
                    {(inc.reporter_name || "Unknown reporter")}
                    {" · "}
                    {fmtDateTime(inc.occurred_at)}
                  </span>
                </div>
                <span className={`sp-pill ${inc.status === "RESOLVED" ? "sp-pill--success" : "sp-pill--neutral"}`}>
                  {titleCase(inc.status)}
                </span>
              </div>

              <div className="sp-handover-body">
                {inc.description && (
                  <div className="sp-handover-note">
                    <span className="sp-handover-note-text">{inc.description}</span>
                  </div>
                )}

                {inc.status === "RESOLVED" && inc.resolution_note && (
                  <div className="sp-handover-note">
                    <span className="sp-handover-note-label">Resolution</span>
                    <span className="sp-handover-note-text">{inc.resolution_note}</span>
                  </div>
                )}

                {inc.status === "OPEN" && (
                  resolveId === inc.id ? (
                    <div>
                      <div className="sp-field" style={{ marginTop: 8 }}>
                        <label>Resolution note (optional)</label>
                        <textarea
                          rows={2}
                          value={resolutionNote}
                          onChange={e => setResolutionNote(e.target.value)}
                          placeholder="How was it resolved?"
                          maxLength={4000}
                        />
                      </div>
                      <div className="sp-form-actions sp-comp-actions">
                        <button className="sp-btn sp-btn--ghost" disabled={resolving} onClick={() => { setResolveId(null); setResolutionNote(""); }}>Cancel</button>
                        <button className="sp-btn sp-btn--primary" disabled={resolving} onClick={() => submitResolve(inc)}>
                          {resolving ? "Resolving…" : "Mark resolved"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="sp-form-actions sp-comp-actions">
                      <button className="sp-btn sp-btn--ghost" onClick={() => { setResolveId(inc.id); setResolutionNote(""); }}>
                        Resolve
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Roster tab ────────────────────────────────────────────────────────────────
// Supervisor view of the versioned daily roster. Mirrors the CORE of the mobile
// `(security)/roster.tsx` flow against /api/roster (all require_supervisor): pick
// a date, see the DAY + NIGHT roster for it (status pill + version + window +
// supervisor + remarks + assignment list), create a DRAFT roster for a shift
// when none exists, add/remove guard assignments on a DRAFT, then Publish.
// On a PUBLISHED roster each assignment shows the guard's accept/decline status
// read-only, and the supervisor can Re-publish (bumps version) or Archive.
// Audit replay, duty start/stop, and replacements are intentionally omitted.
const ROSTER_SHIFTS = ["DAY", "NIGHT"];

function rosterStatusPillClass(s) {
  return s === "PUBLISHED" ? "sp-pill--success" : "sp-pill--neutral";
}

function assignmentStatusPillClass(s) {
  if (s === "ACCEPTED" || s === "COMPLETED") return "sp-pill--success";
  if (s === "DECLINED" || s === "NO_SHOW") return "sp-pill--danger";
  return "sp-pill--neutral";
}

function assignmentStatusLabel(s) {
  switch (s) {
    case "ASSIGNED": return "Assigned";
    case "ACCEPTED": return "Accepted";
    case "DECLINED": return "Declined";
    case "NO_SHOW": return "No show";
    case "COMPLETED": return "Completed";
    default: return s;
  }
}

function RosterCard({ shift, roster, guards, date, onChanged }) {
  const [busy, setBusy] = useState(null); // "create" | "publish" | "archive"
  const [addOpen, setAddOpen] = useState(false);
  const [guardId, setGuardId] = useState("");
  const [area, setArea] = useState("");
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const label = shiftLabel(shift);

  const create = async () => {
    setBusy("create");
    try {
      await securityApi.createRoster({ roster_date: date, shift });
      toast.success(`${label} roster created`);
      await onChanged();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not create roster");
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      const r = await securityApi.publishRoster(roster.id);
      toast.success(`Published v${r?.version ?? ""}`.trim());
      await onChanged();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not publish roster");
    } finally {
      setBusy(null);
    }
  };

  const archive = async () => {
    setBusy("archive");
    try {
      await securityApi.archiveRoster(roster.id);
      toast.success("Roster archived");
      await onChanged();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not archive roster");
    } finally {
      setBusy(null);
    }
  };

  const addGuard = async (e) => {
    e.preventDefault();
    if (!guardId) { toast.error("Select a guard"); return; }
    setSaving(true);
    try {
      await securityApi.upsertAssignment(roster.id, {
        guard_id: guardId,
        assigned_area: area.trim() || null,
      });
      toast.success("Guard assigned");
      setAddOpen(false);
      setGuardId("");
      setArea("");
      await onChanged();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not assign guard");
    } finally {
      setSaving(false);
    }
  };

  const removeAssignment = async (a) => {
    setRemovingId(a.id);
    try {
      await securityApi.deleteAssignment(a.id);
      toast.success("Assignment removed");
      await onChanged();
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not remove assignment");
    } finally {
      setRemovingId(null);
    }
  };

  if (!roster) {
    return (
      <button className="sp-btn sp-btn--ghost sp-roster-create" disabled={busy === "create"} onClick={create}>
        <Plus size={14} /> {busy === "create" ? "Creating…" : `Create ${label} roster`}
      </button>
    );
  }

  const assignments = roster.assignments || [];
  const isDraft = roster.status === "DRAFT";
  const isPublished = roster.status === "PUBLISHED";

  return (
    <div className="sp-comp-card">
      <div className="sp-handover-head">
        <div className="sp-guard-info">
          <span className="sp-guard-name">{fmtTime(roster.shift_starts_at)} – {fmtTime(roster.shift_ends_at)}</span>
          <span className="sp-guard-meta">
            v{roster.version}
            {roster.supervisor_name ? ` · Supervisor: ${roster.supervisor_name}` : ""}
          </span>
        </div>
        <span className={`sp-pill ${rosterStatusPillClass(roster.status)}`}>{roster.status}</span>
      </div>

      <div className="sp-handover-body">
        {roster.remarks && (
          <div className="sp-handover-note">
            <span className="sp-handover-note-text">{roster.remarks}</span>
          </div>
        )}

        {assignments.length === 0 ? (
          <div className="sp-comp-detail-msg">No guards assigned yet.</div>
        ) : (
          assignments.map(a => (
            <div key={a.id} className="sp-comp-check">
              <div className="sp-guard-info">
                <span className="sp-comp-check-label">{a.guard_name || "—"}</span>
                <span className="sp-guard-meta">{a.assigned_area || "No area"}</span>
              </div>
              <span className={`sp-pill ${assignmentStatusPillClass(a.status)}`}>
                {assignmentStatusLabel(a.status)}
              </span>
              {isDraft && (
                <button
                  className="sp-btn sp-btn--sm sp-btn--ghost-red"
                  disabled={removingId === a.id}
                  onClick={() => removeAssignment(a)}
                >
                  {removingId === a.id ? "…" : <Trash2 size={13} />}
                </button>
              )}
            </div>
          ))
        )}

        {isDraft && (
          addOpen ? (
            <form className="sp-form-card sp-roster-add" onSubmit={addGuard}>
              <div className="sp-field">
                <label>Guard</label>
                <select value={guardId} onChange={e => setGuardId(e.target.value)} required>
                  <option value="">Select guard…</option>
                  {guards.map(g => <option key={g.guard_id} value={g.guard_id}>{g.name}</option>)}
                </select>
              </div>
              <div className="sp-field">
                <label>Area (optional)</label>
                <input value={area} onChange={e => setArea(e.target.value)} placeholder="Main gate, Block C…" maxLength={100} />
              </div>
              <div className="sp-form-actions">
                <button type="button" className="sp-btn sp-btn--ghost" onClick={() => { setAddOpen(false); setGuardId(""); setArea(""); }}>Cancel</button>
                <button type="submit" className="sp-btn sp-btn--primary" disabled={saving}>{saving ? "Assigning…" : "Assign guard"}</button>
              </div>
            </form>
          ) : (
            <div className="sp-form-actions sp-comp-actions">
              <button className="sp-btn sp-btn--ghost" onClick={() => setAddOpen(true)}>
                <Plus size={14} /> Add guard
              </button>
              <button className="sp-btn sp-btn--primary" disabled={busy === "publish"} onClick={publish}>
                {busy === "publish" ? "Publishing…" : "Publish roster"}
              </button>
            </div>
          )
        )}

        {isPublished && (
          <div className="sp-form-actions sp-comp-actions">
            <button className="sp-btn sp-btn--ghost" disabled={busy === "archive"} onClick={archive}>
              {busy === "archive" ? "Archiving…" : "Archive"}
            </button>
            <button className="sp-btn sp-btn--primary" disabled={busy === "publish"} onClick={publish}>
              {busy === "publish" ? "Re-publishing…" : "Re-publish"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RosterTab() {
  const [date, setDate] = useState(todayKey());
  const [guards, setGuards] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    securityApi.listGuards({ active: true }).then(data => {
      setGuards(Array.isArray(data) ? data : data.guards || []);
    }).catch(() => {});
  }, []);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(false);
    try {
      const data = await securityApi.listRostersForDate({ date: d });
      setRosters(Array.isArray(data?.rosters) ? data.rosters : []);
    } catch (e) {
      setRosters([]);
      setError(true);
      toast.error(e?.message || "Could not load roster");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  // Keep the highest-version roster per shift if duplicates ever appear.
  const rosterForShift = (shift) => {
    let best = null;
    for (const r of rosters) {
      if (r.shift !== shift) continue;
      if (!best || (r.version ?? 0) >= (best.version ?? 0)) best = r;
    }
    return best;
  };

  return (
    <div>
      <div className="sp-row-between sp-mb">
        <div className="sp-row-between" style={{ gap: 8, justifyContent: "flex-start" }}>
          <input
            type="date"
            className="sp-date-input"
            value={date}
            onChange={e => e.target.value && setDate(e.target.value)}
          />
          {date !== todayKey() && (
            <button className="sp-btn sp-btn--ghost sp-btn--sm" onClick={() => setDate(todayKey())}>Today</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="sp-empty">Loading roster…</div>
      ) : error ? (
        <div className="sp-empty">Could not load roster for {date}.</div>
      ) : (
        ROSTER_SHIFTS.map(shift => (
          <div key={shift} className="sp-roster-shift">
            <div className="sp-section-label">{shiftLabel(shift)} shift</div>
            <RosterCard
              shift={shift}
              roster={rosterForShift(shift)}
              guards={guards}
              date={date}
              onChanged={() => load(date)}
            />
          </div>
        ))
      )}
    </div>
  );
}

// ── Main SecurityPage ─────────────────────────────────────────────────────────
const TABS_ADMIN = [
  { id: "map", label: "Live Map" },
  { id: "sos", label: "SOS" },
  { id: "guards", label: "Guards" },
  { id: "shifts", label: "Shifts" },
  { id: "roster", label: "Roster" },
  { id: "patrols", label: "Patrols" },
  { id: "compliance", label: "Compliance" },
  { id: "handover", label: "Handover" },
  { id: "incidents", label: "Incidents" },
];

// Incharge gets the same console as admin: a SECURITY_INCHARGE is a SUPERVISOR
// guard (services/roles.py), and the guards/shifts endpoints accept
// require_supervisor — so the incharge can manage guards + shifts like on mobile.
const TABS_INCHARGE = [
  { id: "map", label: "Live Map" },
  { id: "sos", label: "SOS" },
  { id: "guards", label: "Guards" },
  { id: "shifts", label: "Shifts" },
  { id: "roster", label: "Roster" },
  { id: "patrols", label: "Patrols" },
  { id: "compliance", label: "Compliance" },
  { id: "handover", label: "Handover" },
  { id: "incidents", label: "Incidents" },
];

export default function SecurityPage({ isAdmin = false }) {
  const tabs = isAdmin ? TABS_ADMIN : TABS_INCHARGE;
  const [tab, setTab] = useState("map");

  return (
    <div className="sp-page">
      <div className="sp-header">
        <h1 className="sp-title"><Shield size={20} /> Security</h1>
        <p className="sp-sub">Live guard map, roster management and patrol reports</p>
      </div>

      <div className="sp-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`sp-tab${tab === t.id ? " active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="sp-body">
        {tab === "map" && (
          <div className="sp-map-wrap">
            <SupervisorMap />
          </div>
        )}
        {tab === "sos" && <SosPanel mode="responder" />}
        {tab === "guards" && <GuardsTab />}
        {tab === "shifts" && <ShiftsTab />}
        {tab === "roster" && <RosterTab />}
        {tab === "patrols" && <PatrolsTab />}
        {tab === "compliance" && <ComplianceTab />}
        {tab === "handover" && <HandoverTab />}
        {tab === "incidents" && <IncidentsTab />}
      </div>
    </div>
  );
}
